import { useState, useEffect, useMemo } from 'react';
import { payrollSettingsApi, MONTH_OPTIONS, LEAVE_SETTING_OPTIONS } from '../api/payrollSettingsApi';
import { appToast } from '../../../../components/AppToast';

export function usePayrollSettings() {
  const [formData, setFormData] = useState({
    payrollStartDate: '2026-04-01',
    payrollYearEndMonth: 'Mar',
    payrollLeaveSetting: 'Monthly Credit Leaves',
    allowLeaveOverAvailable: false,
    updatedBy: null,
    updatedByName: '',
    updatedOn: null,
  });

  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setIsLoading(true);
      try {
        const data = await payrollSettingsApi.getSettings();
        if (mounted && data) {
          setFormData(data);
          setInitialData(data);
        }
      } catch (err) {
        console.error('Error fetching payroll settings:', err);
        appToast.error('Failed to load payroll settings');
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isDirty = useMemo(() => {
    if (!initialData) return false;
    return (
      formData.payrollStartDate !== initialData.payrollStartDate ||
      formData.payrollYearEndMonth !== initialData.payrollYearEndMonth ||
      formData.payrollLeaveSetting !== initialData.payrollLeaveSetting ||
      formData.allowLeaveOverAvailable !== initialData.allowLeaveOverAvailable
    );
  }, [formData, initialData]);

  const handleDiscard = () => {
    if (initialData) {
      setFormData(initialData);
      appToast.info('Changes discarded.');
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    try {
      const res = await payrollSettingsApi.saveSettings(formData);
      if (res?.data) {
        setFormData(res.data);
        setInitialData(res.data);
      }
      appToast.success('Payroll settings saved successfully!');
    } catch (err) {
      console.error('Error saving payroll settings:', err);
      appToast.error('Failed to save payroll settings');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      formData,
      initialData,
      isLoading,
      isSaving,
      isDirty,
      monthOptions: MONTH_OPTIONS,
      leaveSettingOptions: LEAVE_SETTING_OPTIONS,
    },
    actions: {
      handleChange,
      handleSave,
      handleDiscard,
    },
  };
}
